import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { XCircle, CheckCircle, ShieldCheck, WarningCircle, Pause, Play } from '@phosphor-icons/react';
import { animateMarkerTo, pushBreadcrumbPoint } from '../../utils/mapAnimation';
import './security.css';

export default function CollectorSharePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [sharing, setSharing] = useState(true);
  const [position, setPosition] = useState(null);
  const [error, setError] = useState(null);
  const [ended, setEnded] = useState(false);
  const [postCount, setPostCount] = useState(0);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const leafletRef = useRef(null);
  const markerRef = useRef(null);
  const trailRef = useRef(null);   // breadcrumb of the collector's own walked path
  const pointsRef = useRef([]);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);

  // Lazily places the "your location" pin on the first real GPS fix (never
  // at the placeholder Accra-center coordinate the map opens on), then
  // glides it on every fix after that.
  const upsertOwnMarker = (lat, lng) => {
    const L = leafletRef.current, map = mapInstanceRef.current;
    if (!L || !map) return;
    if (!markerRef.current) {
      const icon = L.divIcon({
        html: `<div style="font-size:26px;line-height:1;">📍</div>`,
        className: '', iconSize: [26, 26], iconAnchor: [13, 26],
      });
      markerRef.current = L.marker([lat, lng], { icon }).addTo(map)
        .bindTooltip('Your location', { permanent: true, direction: 'top' });
      pointsRef.current = [[lat, lng]];
      trailRef.current = L.polyline([[lat, lng]], { color: '#16a34a', weight: 2, opacity: 0.5, dashArray: '4 6' }).addTo(map);
      map.setView([lat, lng], 15);
    } else {
      animateMarkerTo(markerRef.current, [lat, lng]);
      pointsRef.current = pushBreadcrumbPoint(pointsRef.current, [lat, lng]);
      trailRef.current?.setLatLngs(pointsRef.current);
      map.setView([lat, lng], map.getZoom(), { animate: true });
    }
  };

  // Start watching GPS
  useEffect(() => {
    if (!sharing || !token) return;

    if (!navigator.geolocation) {
      setError('Geolocation is not supported on this device.');
      return;
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        setPosition({ lat, lng, accuracy });
        upsertOwnMarker(lat, lng);
      },
      (err) => setError(`Location error: ${err.message}`),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharing, token]);

  // Post location to backend every 10s
  useEffect(() => {
    if (!sharing || !position || !token) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

    intervalRef.current = setInterval(() => {
      fetch(`${API_URL}/api/security/collector/${token}/location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat: position.lat, lng: position.lng }),
      }).catch(() => {/* best-effort, no crash */});
      setPostCount(c => c + 1);
    }, 10000);

    return () => clearInterval(intervalRef.current);
  }, [sharing, position, token]);

  // Init map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    let isMounted = true;

    import('leaflet').then(L => {
      if (!isMounted || !mapRef.current) return;

      if (!document.querySelector('#leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }

      // Placeholder center only — no marker goes here. The "Your location"
      // pin is placed by upsertOwnMarker once a real GPS fix comes in, so
      // it's never shown sitting somewhere the collector isn't.
      const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false })
        .setView([5.556, -0.187], 14);

      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', { maxZoom: 19 }).addTo(map);

      leafletRef.current = L;
      mapInstanceRef.current = map;
      if (position) upsertOwnMarker(position.lat, position.lng);
    });

    return () => {
      isMounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = () => {
    if (sharing) {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
      clearInterval(intervalRef.current);
    }
    setSharing(v => !v);
  };

  const handleEnd = () => {
    if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
    clearInterval(intervalRef.current);
    setSharing(false);
    setEnded(true);
    const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';
    fetch(`${API_URL}/api/security/collector/${token}/end`, { method: 'POST' }).catch(() => {});
  };

  if (!token) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: 24 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}><XCircle size={48} /></div>
          <p style={{ color: '#111827', fontWeight: 700 }}>Invalid link — no session token found.</p>
          <p style={{ color: '#6b7280', fontSize: 13 }}>Please use the link sent to you by the school security officer.</p>
        </div>
      </div>
    );
  }

  if (ended) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0fdf4', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}><CheckCircle size={56} /></div>
          <h2 style={{ color: '#15803d', fontWeight: 800, fontSize: 22, margin: '0 0 8px' }}>Location sharing ended</h2>
          <p style={{ color: '#6b7280', fontSize: 14 }}>
            The parent has been notified that the child is safely home. You can close this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Inter', 'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: '#16a34a', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 26 }}><ShieldCheck size={26} /></div>
        <div>
          <p style={{ color: 'white', fontWeight: 700, fontSize: 15, margin: 0 }}>Student Pickup</p>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, margin: 0 }}>Student Pickup — Location Share</p>
        </div>
      </div>

      {/* Info card */}
      <div style={{ background: 'white', margin: 16, borderRadius: 16, padding: 20, border: '1px solid #e5e7eb' }}>
        <p style={{ fontSize: 14, color: '#111827', fontWeight: 700, margin: '0 0 6px' }}>
          You are collecting a student
        </p>
        <p style={{ fontSize: 13, color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
          Your live location is being shared with the student's parent or guardian until they confirm the child has arrived home safely.
        </p>
      </div>

      {/* Map */}
      <div style={{ position: 'relative', flex: 1, minHeight: 300 }}>
        <div ref={mapRef} style={{ height: '100%' }} />
        {!position && !error && (
          <div className="map-waiting-fix" style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.55)', pointerEvents: 'none' }}>
            Getting your location…
          </div>
        )}
      </div>

      {/* Status bar */}
      <div style={{ background: '#f0fdf4', borderTop: '1px solid #bbf7d0', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: sharing ? '#16a34a' : '#9ca3af', animation: sharing ? 'pulse-dot 1.5s infinite' : 'none' }} />
        <span style={{ fontSize: 13, color: sharing ? '#15803d' : '#6b7280', fontWeight: 600 }}>
          {sharing ? `Sharing live — ${postCount} update${postCount !== 1 ? 's' : ''} sent` : 'Location sharing paused'}
        </span>
        {position && (
          <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 'auto', fontFamily: 'monospace' }}>
            {position.lat.toFixed(5)}, {position.lng.toFixed(5)}
          </span>
        )}
      </div>

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', margin: '0 16px 8px', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
          <WarningCircle size={16} /> {error}
        </div>
      )}

      {/* Controls */}
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button
          onClick={handleToggle}
          style={{
            padding: '14px', borderRadius: 12, border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer',
            background: sharing ? '#fef3c7' : '#16a34a',
            color: sharing ? '#d97706' : 'white',
          }}
        >
          {sharing
            ? <><Pause size={16} weight="fill" /> Pause Location Sharing</>
            : <><Play size={16} weight="fill" /> Resume Location Sharing</>}
        </button>
        <button
          onClick={handleEnd}
          style={{
            padding: '12px', borderRadius: 12, border: '1px solid #e5e7eb', fontWeight: 600, fontSize: 14,
            background: 'white', color: '#6b7280', cursor: 'pointer',
          }}
        >
          Child is home — end session
        </button>
      </div>

      <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', padding: '0 16px 20px' }}>
        Location data is only shared with the school and the student's registered guardian. It is not stored beyond today.
      </p>
    </div>
  );
}
